/* eslint-disable consistent-default-export-name/default-import-match-filename */
import HeaderRow from './HeaderRow'
import RowList from './RowList'
import TableElement from './server/elements/Table'
import { type ForwardedRef, forwardRef, useId, useMemo, useRef } from 'react'
import { TableStoreProvider, useExpandable, useIsPivot, usePivotEntities, useResizeContext } from './utils/TableStore'
import type { TableConfig } from './types'

function Header() {
  const pivot = useIsPivot()
  if (pivot) return null
  return <HeaderRow />
}

const TableInner = forwardRef(function TableInner<T extends Record<string, unknown>>(
  { className }: { className?: string },
  ref: ForwardedRef<HTMLTableElement>
) {
  const { resizing, touchEnabled } = useResizeContext()
  const pivot = useIsPivot()
  const expandable = useExpandable()
  return (
    <TableElement
      ref={ref}
      resizing={resizing}
      head={<Header />}
      pivot={pivot}
      expandable={expandable}
      className={className}
      touchEnabled={touchEnabled ?? undefined}
    >
      <RowList />
    </TableElement>
  )
})

function Debug() {
  const { columnWidths, resizing, touchEnabled } = useResizeContext()
  const entities = usePivotEntities()
  return (
    <>
      <div style={{ border: resizing ? '1px solid red' : '1px solid black', marginBlockStart: 50, padding: 5 }}>
        [{touchEnabled ? 'touch' : 'mouse'}]: {JSON.stringify(columnWidths.map((c) => c.width))}
      </div>
      <pre>{JSON.stringify(entities, null, 2)}</pre>
    </>
  )
}
function Table<T extends Record<string, unknown>>({
  columns,
  data,
  getRowId,
  pivot,
  expandable,
  id: id_,
  className,
  pivotLabelColumnConfig,
  debug,
  maxWidth,
}: TableConfig<T> & { debug?: boolean }) {
  const generatedId = useId()
  const id = id_ ?? generatedId
  const config = useMemo(
    () => ({ columns, getRowId, pivot, expandable, id, pivotLabelColumnConfig }),
    [columns, getRowId, pivot, expandable, id, pivotLabelColumnConfig]
  )
  const tableRef = useRef<HTMLTableElement>(null)
  return (
    <TableStoreProvider data={data} config={config} tableRef={tableRef}>
      <TableInner ref={tableRef} className={className} />
      {debug && <Debug />}
    </TableStoreProvider>
  )
}

export default Table
